import { getIn } from 'formik'
import i18next from 'i18next'
import { formatContactAsyncOption } from '../../../api/contacts/formatters'
import contacts from '../../../api/contacts/hooks'
import { LOCATIONS_RESET_CONFIG } from '../../../api/locations/formValuesConfig'
import { store } from '../../../store'
import { setFormFilter } from '../../../store/formFilters'
import { renderAreaUnit, renderCurrency } from '../../../utils/property'
import { CONTACT_JOB_TITLE, REAL_ESTATE_TYPES } from '../../constants'
import responseFields from '../../constants/responseFields'
import { companyProfileFields } from '../company/fields'
import {
    baseAsyncSelectInput,
    baseCheckboxInput,
    baseDateInput,
    baseMultiSelectInput,
    baseNumberInput,
    baseNumeralInput,
    basePriceInput,
    baseSelectInput,
    baseTextAreaInput,
    baseTextInput,
    phoneNumber,
} from '../fieldTypes'
import { contactDetailSearchFields, contactGeneralFields, contactNoteFields, contactOtherInfoFields, contactProfileFields } from './fields'

export const contactGeneralFieldTypes = () => {
    const { t } = i18next

    return {
        [contactGeneralFields.ID]: {
            ...baseTextInput,
            name: contactGeneralFields.ID,
            label: t('form.contact.general.id'),
            disabled: true,
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.CONTACT_TITLE_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.CONTACT_TITLE_ID,
            label: t('form.contact.title'),
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.FIRST_NAME]: {
            ...baseTextInput,
            name: contactGeneralFields.FIRST_NAME,
            label: t('app.common.firstName'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.MIDDLE_NAME]: {
            ...baseTextInput,
            name: contactGeneralFields.MIDDLE_NAME,
            label: t('form.contact.general.middleName'),
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.LAST_NAME]: {
            ...baseTextInput,
            name: contactGeneralFields.LAST_NAME,
            label: t('app.common.lastName'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.INITIAL_CONTACT_DATE]: {
            ...baseDateInput,
            name: contactGeneralFields.INITIAL_CONTACT_DATE,
            label: t('app.common.contact.establishmentDate'),
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.CONTACT_PRIORITY_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.CONTACT_PRIORITY_ID,
            label: t('form.contact.general.priority'),
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.DATA_SOURCE_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.DATA_SOURCE_ID,
            label: t('app.common.contact.dataSource'),
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.PHONE_NUMBER]: {
            ...phoneNumber,
            name: contactGeneralFields.PHONE_NUMBER,
            label: t('app.common.phoneNumber'),
        },
        [contactGeneralFields.CONTACT_PHONE_TYPE_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.CONTACT_PHONE_TYPE_ID,
            label: t('form.contact.general.phoneType'),
        },
        [contactGeneralFields.EMAIL]: {
            ...baseTextInput,
            name: contactGeneralFields.EMAIL,
            label: t('app.common.email.label'),
        },
        [contactGeneralFields.CONTACT_EMAIL_TYPE_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.CONTACT_EMAIL_TYPE_ID,
            label: t('form.contact.general.emailType'),
        },
        [contactGeneralFields.NEWSLETTER_APPROVAL]: {
            ...baseCheckboxInput,
            name: contactGeneralFields.NEWSLETTER_APPROVAL,
            label: t('form.contact.general.newsletter.label'),
            tooltip: t('form.contact.general.newsletter.tooltip'),
        },
        [contactGeneralFields.STREET]: {
            ...baseTextInput,
            name: contactGeneralFields.STREET,
            label: t('app.common.street'),
        },
        [contactGeneralFields.STREET_NUMBER]: {
            ...baseTextInput,
            name: contactGeneralFields.STREET_NUMBER,
            label: t('app.common.streetNumber'),
        },
        [contactGeneralFields.ZIP_CODE]: {
            ...baseTextInput,
            name: contactGeneralFields.ZIP_CODE,
            label: t('form.contact.general.zipCode'),
        },
        [contactGeneralFields.CITY]: {
            ...baseTextInput,
            name: contactGeneralFields.CITY,
            label: t('app.common.cityMunicipality'),
        },
        [contactGeneralFields.CONTACT_COUNTRY_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.CONTACT_COUNTRY_ID,
            label: t('app.common.country'),
        },
        [contactGeneralFields.CONTACT_STATUS_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.CONTACT_STATUS_ID,
            label: t('app.common.status'),
            required: true,
            colProps: {
                sm: 12,
            },
        },
        [contactGeneralFields.AGENT_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.AGENT_ID,
            label: t('app.common.agent'),
            required: true,
        },
        [contactGeneralFields.CONTACT_QUERY_CATEGORY_ID]: {
            ...baseSelectInput,
            name: contactGeneralFields.CONTACT_QUERY_CATEGORY_ID,
            label: t('app.common.contact.queryCategory'),
        },
    }
}

export const contactOtherInfoFieldTypes = () => {
    const { t } = i18next

    return {
        [contactOtherInfoFields.CONTACT_GENDER_ID]: {
            ...baseSelectInput,
            name: contactOtherInfoFields.CONTACT_GENDER_ID,
            label: t('app.common.gender'),
            colProps: {
                sm: 12,
            },
        },
        [contactOtherInfoFields.OIB]: {
            ...baseNumberInput,
            name: contactOtherInfoFields.OIB,
            label: t('app.common.pin'),
            colProps: {
                sm: 12,
            },
        },
        [contactOtherInfoFields.CONTACT_DESCRIPTION_ID]: {
            ...baseSelectInput,
            name: contactOtherInfoFields.CONTACT_DESCRIPTION_ID,
            label: t('app.common.contact.description'),
            colProps: {
                sm: 12,
            },
        },
        [contactOtherInfoFields.COMPANY_ID]: {
            ...baseAsyncSelectInput,
            colProps: {
                sm: 12,
                xxl: 3,
            },
            name: contactOtherInfoFields.COMPANY_ID,
            label: t('app.common.company.label'),
            searchCriteria: ['name'],
            fixedCriteria: {
                contactType: 'company',
            },
            noLimit: true,
            tooltip: t('form.contact.other.company.tooltip'),
            useEntityHook: contacts.GetContacts.useQuery,
            formatOption: formatContactAsyncOption,
            handleChange: (e, formik, fieldName) => {
                // clear contact_job_title_id, ownership_percentage & employment_type fields when company_id is cleared
                if (!e) {
                    const fieldsToReset = ['contact_job_title_id', 'ownership_percentage', 'employment_type']

                    fieldsToReset.forEach((field) => {
                        const targetFieldName = fieldName.replace('company_id', field)
                        const currentValue = getIn(formik.values, targetFieldName)

                        if (currentValue) {
                            formik.setFieldValue(targetFieldName, '')
                        }
                    })
                }
            },
        },
        [contactOtherInfoFields.CONTACT_JOB_TITLE_ID]: {
            ...baseSelectInput,
            name: contactOtherInfoFields.CONTACT_JOB_TITLE_ID,
            label: t('app.common.jobTitle'),
            disabled: true,
            handleInputDisable: (formik, fieldName) => {
                // fieldName contains: subform.inputGroup.index.name
                // example: otherInfo.contact_person_company_group.0.contact_job_title_id
                // since inputGroup is fieldArray in "otherInfo" subform (eg. user can add multiple inputGroups),
                // replace method is used to preserve inputGroup index & just replace current field with field that controls enable/disable
                const companyIdFieldName = fieldName.replace('contact_job_title_id', 'company_id')
                const companyValue = getIn(formik.values, companyIdFieldName)

                return !companyValue
            },
            handleChange: (value, formik, fieldName) => {
                // clear ownership_percentage field when contact_job_title_id is cleared or changed to job title that does not support ownership_percentage
                const ownershipFieldName = fieldName.replace('contact_job_title_id', 'ownership_percentage')
                const ownershipValue = getIn(formik.values, ownershipFieldName)

                if (ownershipValue && ![CONTACT_JOB_TITLE.ARTISAN, CONTACT_JOB_TITLE.OWNER].includes(Number(value))) {
                    formik.setFieldValue(ownershipFieldName, '')
                }
            },
            colProps: {
                sm: 12,
                xxl: 3,
            },
        },
        [contactOtherInfoFields.OWNERSHIP_PERCENTAGE]: {
            ...baseNumeralInput,
            name: contactOtherInfoFields.OWNERSHIP_PERCENTAGE,
            label: t('app.common.contact.ownershipPercentage'),
            tooltip: t('app.common.contact.inDepthAnalysis.tooltip'),
            disabled: true,
            handleInputDisable: (formik, fieldName) => {
                // similar to CONTACT_JOB_TITLE_ID field, but is enabled/disabled according to contact_job_title select
                const contactJobTitleFieldName = fieldName.replace('ownership_percentage', 'contact_job_title_id')
                const contactJobTitleValue = getIn(formik.values, contactJobTitleFieldName)
                const shouldDisable = ![CONTACT_JOB_TITLE.ARTISAN, CONTACT_JOB_TITLE.OWNER].includes(Number(contactJobTitleValue))

                return shouldDisable
            },
            colProps: {
                sm: 12,
                xxl: 3,
            },
        },
        [contactOtherInfoFields.EMPLOYMENT_TYPE]: {
            ...baseTextInput,
            name: contactOtherInfoFields.EMPLOYMENT_TYPE,
            label: t('app.common.contact.employmentType'),
            disabled: true,
            handleInputDisable: (formik, fieldName) => {
                const contactJobTitleFieldName = fieldName.replace('employment_type', 'contact_job_title_id')
                const contactJobTitleValue = getIn(formik.values, contactJobTitleFieldName)

                return !contactJobTitleValue
            },
            colProps: {
                sm: 12,
                xxl: 3,
            },
        },
        [contactOtherInfoFields.BIRTH_PLACE]: {
            ...baseTextInput,
            colProps: {
                sm: 12,
                xxl: 3,
            },
            name: contactOtherInfoFields.BIRTH_PLACE,
            label: t('app.common.contact.birthplace'),
            tooltip: t('app.common.contact.inDepthAnalysis.tooltip'),
        },
        [contactOtherInfoFields.BIRTH_COUNTRY_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
                xxl: 3,
            },
            name: contactOtherInfoFields.BIRTH_COUNTRY_ID,
            label: t('app.common.contact.countryOfBirth'),
            tooltip: t('app.common.contact.inDepthAnalysis.tooltip'),
        },
        [contactOtherInfoFields.BIRTH_DATE]: {
            ...baseDateInput,
            colProps: {
                sm: 12,
                xxl: 3,
            },
            name: contactOtherInfoFields.BIRTH_DATE,
            label: t('app.common.contact.birthDate'),
        },
        [contactOtherInfoFields.CONTACT_MARTIAL_STATUS_ID]: {
            ...baseSelectInput,
            colProps: {
                sm: 12,
                xxl: 3,
            },
            name: contactOtherInfoFields.CONTACT_MARTIAL_STATUS_ID,
            label: t('app.common.contact.maritalStatus'),
        },
        [contactOtherInfoFields.INITIAL_CALL_INFO]: {
            ...baseTextAreaInput,
            name: contactOtherInfoFields.INITIAL_CALL_INFO,
            label: t('form.contact.other.initialCallInfo'),
            colProps: {
                sm: 12,
            },
            inputProps: {
                style: { resize: 'none', height: '170px' },
            },
        },
        [contactOtherInfoFields.NOTES]: {
            ...baseTextAreaInput,
            name: contactOtherInfoFields.NOTES,
            label: t('app.common.remark'),
            colProps: {
                sm: 12,
            },
            inputProps: {
                style: { resize: 'none', height: '170px' },
            },
        },
    }
}

export const contactProfileFieldTypes = (appSettings, isEdit, module = 'contact') => {
    const { t } = i18next

    const definitionFieldName = module === 'company' ? companyProfileFields.COMPANY_DEFINITION_ID : contactProfileFields.CONTACT_DEFINITION_ID
    const definitionLabel = module === 'company' ? t('form.company.profile.definition') : t('app.common.contact.definition')

    return {
        [contactProfileFields.CONTACT_PROFILE_SERVICE_ID]: {
            ...baseSelectInput,
            name: contactProfileFields.CONTACT_PROFILE_SERVICE_ID,
            label: t('app.common.service'),
            required: true,
            disabled: isEdit,
            colProps: {
                sm: 12,
            },
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'contactProfile', field: 'propertyServiceId', value }))
            },
        },
        [contactProfileFields.PROPERTY_TYPE_ID]: {
            ...baseSelectInput,
            name: contactProfileFields.PROPERTY_TYPE_ID,
            label: t('app.common.property.type'),
            required: true,
            disabled: isEdit,
            colProps: {
                sm: 12,
            },
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'contactProfile', field: 'propertyTypeId', value }))
            },
        },
        [contactProfileFields.PROPERTY_SUB_TYPE_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.PROPERTY_SUB_TYPE_ID,
            label: t('app.common.property.subtype'),
            tooltip: t('app.common.property.subtype.tooltip'),
            handleInputDisable: (formik) => {
                const propertyTypeId = getIn(formik?.values?.profile, contactProfileFields.PROPERTY_TYPE_ID)
                return !propertyTypeId
            },
            colProps: {
                sm: 12,
            },
        },
        [contactProfileFields.CONTACT_PROFILE_STATUS_ID]: {
            ...baseSelectInput,
            name: contactProfileFields.CONTACT_PROFILE_STATUS_ID,
            label: t('form.contact.profile.profileStatus'),
            colProps: {
                sm: 12,
            },
        },
        [contactProfileFields.CONTACT_FINANCING_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.CONTACT_FINANCING_ID,
            label: t('app.common.contact.financing'),
            colProps: {
                sm: 12,
            },
        },
        [contactProfileFields.CONTACT_PROFILE_DURATION_ID]: {
            ...baseSelectInput,
            name: contactProfileFields.CONTACT_PROFILE_DURATION_ID,
            label: t('app.common.contact.realizationTime'),
            colProps: {
                sm: 12,
            },
        },
        [definitionFieldName]: {
            ...baseSelectInput,
            name: definitionFieldName,
            label: definitionLabel,
            colProps: {
                sm: 12,
            },
        },
        [contactProfileFields.CONTACT_PROFILE_REMARK_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.CONTACT_PROFILE_REMARK_ID,
            label: t('app.common.contact.comment'),
            colProps: {
                sm: 12,
            },
        },
        [contactProfileFields.COUNTRY_ID]: {
            ...baseSelectInput,
            name: contactProfileFields.COUNTRY_ID,
            label: t('app.common.country'),
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'country', value }))

                LOCATIONS_RESET_CONFIG.country.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    // county is not multiselect field
                    formik.setFieldValue(`profile.${contactProfileFields[fieldKey]}`, field === 'county' ? '' : [])
                })
            },
        },
        [contactProfileFields.COUNTY_ID]: {
            ...baseSelectInput,
            name: contactProfileFields.COUNTY_ID,
            label: t('app.common.county'),
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'county', value }))

                LOCATIONS_RESET_CONFIG.county.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`profile.${contactProfileFields[fieldKey]}`, [])
                })
            },
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values?.profile, contactProfileFields.COUNTRY_ID)
                return !countryValue || countryValue.length === 0
            },
        },
        [contactProfileFields.CITY_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.CITY_ID,
            label: t('app.common.cityMunicipality'),
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'city', value }))

                LOCATIONS_RESET_CONFIG.city.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`profile.${contactProfileFields[fieldKey]}`, [])
                })
            },
            handleInputDisable: (formik) => {
                const countyValue = getIn(formik?.values?.profile, contactProfileFields.COUNTY_ID)
                return !countyValue || countyValue.length === 0
            },
        },
        [contactProfileFields.WIDER_AREA_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.WIDER_AREA_ID,
            label: t('app.common.widerArea'),
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'widerArea', value }))

                LOCATIONS_RESET_CONFIG.wider_area.forEach((field) => {
                    const fieldKey = `${field.toUpperCase()}_ID`
                    formik.setFieldValue(`profile.${contactProfileFields[fieldKey]}`, [])
                })
            },
            handleInputDisable: (formik) => {
                const cityValue = getIn(formik?.values?.profile, contactProfileFields.CITY_ID)
                return !cityValue || cityValue.length === 0
            },
        },
        [contactProfileFields.QUARTER_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.QUARTER_ID,
            label: t('app.common.quarter'),
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'quarter', value }))
            },
            handleInputDisable: (formik) => {
                const widerAreaValue = getIn(formik?.values?.profile, contactProfileFields.WIDER_AREA_ID)
                return !widerAreaValue || widerAreaValue.length === 0
            },
        },
        [contactProfileFields.ISLAND_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.ISLAND_ID,
            label: t('app.common.island'),
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'island', value }))
            },
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values?.profile, contactProfileFields.COUNTRY_ID)
                return !countryValue
            },
        },
        [contactProfileFields.PRICE_FROM]: {
            ...basePriceInput,
            name: contactProfileFields.PRICE_FROM,
            label: t('form.contact.profile.priceFrom', { currency: renderCurrency(appSettings?.currency) }),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.PRICE_TO]: {
            ...basePriceInput,
            name: contactProfileFields.PRICE_TO,
            label: t('form.contact.profile.priceTo', { currency: renderCurrency(appSettings?.currency) }),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.ROOMS_FROM]: {
            ...baseTextInput,
            name: contactProfileFields.ROOMS_FROM,
            label: t('form.contact.profile.roomsFrom'),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.ROOMS_TO]: {
            ...baseTextInput,
            name: contactProfileFields.ROOMS_TO,
            label: t('form.contact.profile.roomsTo'),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.BEDROOMS_FROM]: {
            ...baseTextInput,
            name: contactProfileFields.BEDROOMS_FROM,
            label: t('form.contact.profile.bedroomsFrom'),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.BEDROOMS_TO]: {
            ...baseTextInput,
            name: contactProfileFields.BEDROOMS_TO,
            label: t('form.contact.profile.bedroomsTo'),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.AREA_FROM]: {
            ...baseNumeralInput,
            name: contactProfileFields.AREA_FROM,
            label: t('form.contact.profile.areaFrom', { unit: renderAreaUnit(appSettings?.areaUnit) }),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.AREA_TO]: {
            ...baseNumeralInput,
            name: contactProfileFields.AREA_TO,
            label: t('form.contact.profile.areaTo', { unit: renderAreaUnit(appSettings?.areaUnit) }),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.CONSTRUCTION_YEAR_FROM]: {
            ...baseTextInput,
            name: contactProfileFields.CONSTRUCTION_YEAR_FROM,
            label: t('form.contact.profile.constructionYearFrom'),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.CONSTRUCTION_YEAR_TO]: {
            ...baseTextInput,
            name: contactProfileFields.CONSTRUCTION_YEAR_TO,
            label: t('form.contact.profile.constructionYearTo'),
            colProps: {
                sm: 12,
                xxl: 6,
            },
        },
        [contactProfileFields.PROPERTY_FLOOR_POSITION_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.PROPERTY_FLOOR_POSITION_ID,
            label: t('app.common.floorPosition'),
        },
        [contactProfileFields.FLOOR_NO_FROM]: {
            ...baseTextInput,
            name: contactProfileFields.FLOOR_NO_FROM,
            label: t('form.contact.profile.floorFrom'),
        },
        [contactProfileFields.FLOOR_NO_TO]: {
            ...baseTextInput,
            name: contactProfileFields.FLOOR_NO_TO,
            label: t('form.contact.profile.floorTo'),
        },
        [contactProfileFields.EXCLUDE_BASEMENT]: {
            ...baseCheckboxInput,
            name: contactProfileFields.EXCLUDE_BASEMENT,
            label: t('app.common.contact.excludeGroundFloor'),
        },
        [contactProfileFields.EXCLUDE_LAST_FLOOR]: {
            ...baseCheckboxInput,
            name: contactProfileFields.EXCLUDE_LAST_FLOOR,
            label: t('app.common.contact.excludeLastFloor'),
        },
        [contactProfileFields.AUTOMATIC_OFFER_ENABLED]: {
            ...baseCheckboxInput,
            name: contactProfileFields.AUTOMATIC_OFFER_ENABLED,
            label: t('app.common.contact.automaticOfferEnabled'),
            colProps: {
                sm: 12,
            },
        },
        [contactProfileFields.PROPERTY_SPACE_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.PROPERTY_SPACE_ID,
            label: t('app.common.property.space'),
            handleInputDisable: (formik) => {
                const propertyTypeId = getIn(formik?.values?.profile, contactProfileFields.PROPERTY_TYPE_ID)
                return !propertyTypeId
            },
        },
        [contactProfileFields.SPACE_COUNT]: {
            ...baseTextInput,
            name: contactProfileFields.SPACE_COUNT,
            label: t('app.common.spaceCount'),
        },
        [contactProfileFields.SPACE_AREA]: {
            ...baseTextInput,
            name: contactProfileFields.SPACE_AREA,
            label: t('app.common.spaceArea'),
        },
        [contactProfileFields.PROPERTY_DESCRIPTIONS_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.PROPERTY_DESCRIPTIONS_ID,
            label: t('app.common.property.description'),
            handleInputDisable: (formik) => {
                const propertyTypeId = getIn(formik?.values?.profile, contactProfileFields.PROPERTY_TYPE_ID)
                const propertyServiceId = getIn(formik?.values?.profile, contactProfileFields.CONTACT_PROFILE_SERVICE_ID)
                return !propertyTypeId || !propertyServiceId
            },
        },
        [contactProfileFields.PROPERTY_EQUIPMENT_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.PROPERTY_EQUIPMENT_ID,
            label: t('app.common.equipment'),
            handleInputDisable: (formik) => {
                const propertyTypeId = getIn(formik?.values?.profile, contactProfileFields.PROPERTY_TYPE_ID)
                return !propertyTypeId
            },
        },
        [contactProfileFields.PROPERTY_UTILITY_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.PROPERTY_UTILITY_ID,
            label: t('app.common.utility'),
            handleInputDisable: (formik) => {
                const propertyTypeId = getIn(formik?.values?.profile, contactProfileFields.PROPERTY_TYPE_ID)
                return !propertyTypeId
            },
        },
        [contactProfileFields.PROPERTY_HEATING_ID]: {
            ...baseMultiSelectInput,
            name: contactProfileFields.PROPERTY_HEATING_ID,
            label: t('app.common.heating'),
            handleInputDisable: (formik) => {
                const propertyTypeId = getIn(formik?.values?.profile, contactProfileFields.PROPERTY_TYPE_ID)
                return !propertyTypeId
            },
        },
        [contactProfileFields.SPECIAL_REQUEST]: {
            ...baseTextAreaInput,
            name: contactProfileFields.SPECIAL_REQUEST,
            label: t('app.common.contact.specialRequests'),
            colProps: {
                sm: 12,
            },
            inputProps: {
                style: { resize: 'none', height: '170px' },
            },
        },
        [contactProfileFields.PROPERTY_FLOOR_ID]: {
            ...baseSelectInput,
            name: contactProfileFields.PROPERTY_FLOOR_ID,
            label: t('app.common.numberOfFloors'),
            handleInputDisable: (formik) => {
                const propertyTypeId = getIn(formik?.values?.profile, contactProfileFields.PROPERTY_TYPE_ID)
                return !propertyTypeId
            },
        },
    }
}

export const contactNoteFieldTypes = () => {
    const { t } = i18next

    return {
        [contactNoteFields.NOTE_TITLE]: {
            ...baseTextInput,
            name: contactNoteFields.NOTE_TITLE,
            label: t('app.common.title'),
            colProps: {
                sm: 12,
            },
        },
        [contactNoteFields.NOTE_DATE]: {
            ...baseDateInput,
            name: contactNoteFields.NOTE_DATE,
            label: t('app.common.date'),
            colProps: {
                sm: 12,
            },
        },
        [contactNoteFields.NOTE]: {
            ...baseTextAreaInput,
            name: contactNoteFields.NOTE,
            label: t('form.notes.note'),
            colProps: {
                sm: 12,
            },
            inputProps: {
                style: { resize: 'none', height: '127px' },
            },
        },
        [contactNoteFields.CONTACT_NOTE_AGENT_ID]: {
            ...baseSelectInput,
            name: contactNoteFields.CONTACT_NOTE_AGENT_ID,
            label: t('app.common.agent'),
            colProps: {
                sm: 12,
            },
        },
    }
}

export const contactDetailSearchFieldTypes = () => {
    const { t } = i18next

    return {
        [contactDetailSearchFields.NAME]: {
            ...baseTextInput,
            name: contactDetailSearchFields.NAME,
            label: t('app.common.contact.nameAndSurname'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CONTACT_PHONE]: {
            ...baseTextInput,
            name: contactDetailSearchFields.CONTACT_PHONE,
            label: t('app.common.phoneNumber'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CONTACT_EMAIL]: {
            ...baseTextInput,
            name: contactDetailSearchFields.CONTACT_EMAIL,
            label: t('app.common.email.label'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.AGENT]: {
            ...baseSelectInput,
            name: contactDetailSearchFields.AGENT,
            label: t('app.common.agent'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.INITIAL_CONTACT_DATE_FROM]: {
            ...baseDateInput,
            name: contactDetailSearchFields.INITIAL_CONTACT_DATE_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [contactDetailSearchFields.INITIAL_CONTACT_DATE_TO]: {
            ...baseDateInput,
            name: contactDetailSearchFields.INITIAL_CONTACT_DATE_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [contactDetailSearchFields.LAST_ACTIVITY_DATE_FROM]: {
            ...baseDateInput,
            name: contactDetailSearchFields.LAST_ACTIVITY_DATE_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [contactDetailSearchFields.LAST_ACTIVITY_DATE_TO]: {
            ...baseDateInput,
            name: contactDetailSearchFields.LAST_ACTIVITY_DATE_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [contactDetailSearchFields.CONTACT_COUNTRY]: {
            ...baseSelectInput,
            name: contactDetailSearchFields.CONTACT_COUNTRY,
            label: t('app.common.country'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CITY]: {
            ...baseTextInput,
            name: contactDetailSearchFields.CITY,
            label: t('app.common.cityMunicipality'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CONTACT_STATUS]: {
            ...baseMultiSelectInput,
            name: contactDetailSearchFields.CONTACT_STATUS,
            label: t('app.common.status'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CONTACT_PROFILE_SERVICE]: {
            ...baseSelectInput,
            name: contactDetailSearchFields.CONTACT_PROFILE_SERVICE,
            label: t('form.contact.contactDetailSearch.profileType'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CONTACT_PROFILE_STATUS]: {
            ...baseSelectInput,
            name: contactDetailSearchFields.CONTACT_PROFILE_STATUS,
            label: t('app.common.contact.profileStatus'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CONTACT_QUERY_CATEGORY]: {
            ...baseSelectInput,
            name: contactDetailSearchFields.CONTACT_QUERY_CATEGORY,
            label: t('app.common.contact.queryCategory'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.CONTACT_PRIORITY]: {
            ...baseSelectInput,
            name: contactDetailSearchFields.CONTACT_PRIORITY,
            label: t('app.common.contact.priority'),
            colProps: {
                sm: 12,
            },
        },
        [contactDetailSearchFields.DATA_SOURCE]: {
            ...baseSelectInput,
            name: contactDetailSearchFields.DATA_SOURCE,
            label: t('app.common.contact.dataSource'),
            colProps: {
                sm: 12,
            },
        },
    }
}

export const contactProfileDetailSearchFieldTypes = () => {
    const { t } = i18next

    return {
        [responseFields.PROPERTY_TYPE]: {
            ...baseMultiSelectInput,
            name: responseFields.PROPERTY_TYPE,
            label: t('app.common.property.type'),
            handleChange: (value) => {
                store.dispatch(setFormFilter({ context: 'contactProfile', field: 'propertyTypeId', value }))
            },
            colProps: {
                sm: 12,
            },
        },
        [responseFields.PROPERTY_SUB_TYPE]: {
            ...baseMultiSelectInput,
            name: responseFields.PROPERTY_SUB_TYPE,
            label: t('app.common.property.subtype'),
            colProps: {
                sm: 12,
            },
        },
        [responseFields.PRICE_FROM]: {
            ...baseTextInput,
            name: responseFields.PRICE_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [responseFields.PRICE_TO]: {
            ...baseTextInput,
            name: responseFields.PRICE_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [responseFields.ROOMS_FROM]: {
            ...baseTextInput,
            name: responseFields.ROOMS_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [responseFields.ROOMS_TO]: {
            ...baseTextInput,
            name: responseFields.ROOMS_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [responseFields.BEDROOMS_FROM]: {
            ...baseTextInput,
            name: responseFields.BEDROOMS_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [responseFields.BEDROOMS_TO]: {
            ...baseTextInput,
            name: responseFields.BEDROOMS_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [responseFields.CONTACT_PROFILE_DURATION]: {
            ...baseSelectInput,
            name: responseFields.CONTACT_PROFILE_DURATION,
            label: t('app.common.contact.realizationTime'),
            colProps: {
                sm: 12,
            },
        },
        [responseFields.AREA_FROM]: {
            ...baseTextInput,
            name: responseFields.AREA_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
        },
        [responseFields.AREA_TO]: {
            ...baseTextInput,
            name: responseFields.AREA_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
        },
        [responseFields.FLOOR_FROM]: {
            ...baseTextInput,
            name: responseFields.FLOOR_FROM,
            placeholder: t('app.common.from'),
            colProps: {
                sm: 6,
                className: 'pe-0',
            },
            handleInputDisable: (formik) => {
                const propertyTypeValue = getIn(formik?.values?.contactProfile, responseFields.PROPERTY_TYPE)
                return propertyTypeValue?.some((value) => [REAL_ESTATE_TYPES.house.toString(), REAL_ESTATE_TYPES.land.toString()].includes(value))
            },
        },
        [responseFields.FLOOR_TO]: {
            ...baseTextInput,
            name: responseFields.FLOOR_TO,
            placeholder: t('app.common.to'),
            colProps: {
                sm: 6,
            },
            handleInputDisable: (formik) => {
                const propertyTypeValue = getIn(formik?.values?.contactProfile, responseFields.PROPERTY_TYPE)
                return propertyTypeValue?.some((value) => [REAL_ESTATE_TYPES.house.toString(), REAL_ESTATE_TYPES.land.toString()].includes(value))
            },
        },
        [responseFields.COUNTRY]: {
            ...baseSelectInput,
            name: responseFields.COUNTRY,
            label: t('app.common.country'),
            colProps: {
                sm: 12,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'country', value }))

                LOCATIONS_RESET_CONFIG.country.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(`contactProfile.${responseFields[fieldKey]}`, [])
                })
            },
        },
        [responseFields.COUNTY]: {
            ...baseMultiSelectInput,
            name: responseFields.COUNTY,
            label: t('app.common.county'),
            colProps: {
                sm: 12,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'county', value }))

                LOCATIONS_RESET_CONFIG.county.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(`contactProfile.${responseFields[fieldKey]}`, [])
                })
            },
            handleInputDisable: (formik) => {
                const countryValue = getIn(formik?.values?.contactProfile, responseFields.COUNTRY)
                return !countryValue || countryValue.length === 0
            },
        },
        [responseFields.CITY]: {
            ...baseMultiSelectInput,
            name: responseFields.CITY,
            label: t('app.common.cityMunicipality'),
            colProps: {
                sm: 12,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'city', value }))

                LOCATIONS_RESET_CONFIG.city.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(`contactProfile.${responseFields[fieldKey]}`, [])
                })
            },
            handleInputDisable: (formik) => {
                const countyValue = getIn(formik?.values?.contactProfile, responseFields.COUNTY)
                return !countyValue || countyValue.length === 0
            },
        },
        [responseFields.WIDER_AREA]: {
            ...baseMultiSelectInput,
            name: responseFields.WIDER_AREA,
            label: t('app.common.widerArea'),
            colProps: {
                sm: 12,
            },
            handleChange: (value, formik) => {
                store.dispatch(setFormFilter({ context: 'location', field: 'widerArea', value }))

                LOCATIONS_RESET_CONFIG.wider_area.forEach((field) => {
                    const fieldKey = field.toUpperCase()
                    formik.setFieldValue(`contactProfile.${responseFields[fieldKey]}`, [])
                })
            },
            handleInputDisable: (formik) => {
                const cityValue = getIn(formik?.values?.contactProfile, responseFields.CITY)
                return !cityValue || cityValue.length === 0
            },
        },
        [responseFields.QUARTER]: {
            ...baseMultiSelectInput,
            name: responseFields.QUARTER,
            label: t('app.common.quarter'),
            colProps: {
                sm: 12,
            },
            handleInputDisable: (formik) => {
                const widerAreaValue = getIn(formik?.values?.contactProfile, responseFields.WIDER_AREA)
                return !widerAreaValue || widerAreaValue.length === 0
            },
        },
        [responseFields.PROPERTY_FLOOR]: {
            ...baseMultiSelectInput,
            name: responseFields.PROPERTY_FLOOR,
            label: t('app.common.numberOfFloors'),
            colProps: {
                sm: 12,
            },
            handleInputDisable: (formik) => {
                const propertyTypeValue = getIn(formik?.values?.contactProfile, responseFields.PROPERTY_TYPE)
                return propertyTypeValue?.some((value) =>
                    [REAL_ESTATE_TYPES.officeSpace.toString(), REAL_ESTATE_TYPES.land.toString(), REAL_ESTATE_TYPES.garage.toString()].includes(value)
                )
            },
        },
    }
}
